import { questionnaireRequirementsToCandidateRequirements } from 'clients/Communication/questionnaireConfigurationMappers';
import { CandidateRequirementsDTO, QuestionKey } from 'clients/Communication/Questionnaire.dto';
import { useCallback, useEffect, useState } from 'react';
import { useQuestionnaireConfigurationQuery } from '../useQuestionnaireConfiguration';
import { UseQuestionnaireParams } from './QuestionnairePreviewDialog.types';

const buildQuestionnaireState = (requirements: CandidateRequirementsDTO): CandidateRequirementsDTO => ({
  [QuestionKey.READY_FOR_JOB_OFFERS]: {
    ...requirements[QuestionKey.READY_FOR_JOB_OFFERS],
    is_required:
      requirements[QuestionKey.READY_FOR_JOB_OFFERS]?.is_required ??
      getShouldShowQuestion(requirements, QuestionKey.READY_FOR_JOB_OFFERS),
  },
  [QuestionKey.CERTIFICATES_AND_LICENSES]: {
    ...requirements[QuestionKey.CERTIFICATES_AND_LICENSES],
    is_required:
      requirements[QuestionKey.CERTIFICATES_AND_LICENSES]?.is_required ??
      getShouldShowQuestion(requirements, QuestionKey.CERTIFICATES_AND_LICENSES),
  },
  [QuestionKey.SALARY]: {
    ...requirements[QuestionKey.SALARY],
    is_required:
      requirements[QuestionKey.SALARY]?.is_required ?? getShouldShowQuestion(requirements, QuestionKey.SALARY),
  },
  [QuestionKey.SHIFTS]: {
    ...requirements[QuestionKey.SHIFTS],
    is_required:
      requirements[QuestionKey.SHIFTS]?.is_required ?? getShouldShowQuestion(requirements, QuestionKey.SHIFTS),
  },
  [QuestionKey.AVAILABLE_DAYS]: {
    ...requirements[QuestionKey.AVAILABLE_DAYS],
    is_required:
      requirements[QuestionKey.AVAILABLE_DAYS]?.is_required ??
      getShouldShowQuestion(requirements, QuestionKey.AVAILABLE_DAYS),
  },
  [QuestionKey.LIMITATIONS_AND_PREFERENCES]: {
    ...requirements[QuestionKey.LIMITATIONS_AND_PREFERENCES],
    is_required:
      requirements[QuestionKey.LIMITATIONS_AND_PREFERENCES]?.is_required ??
      getShouldShowQuestion(requirements, QuestionKey.LIMITATIONS_AND_PREFERENCES),
  },
  [QuestionKey.WORKING_HOURS_PER_WEEK]: {
    ...requirements[QuestionKey.WORKING_HOURS_PER_WEEK],
    is_required:
      requirements[QuestionKey.WORKING_HOURS_PER_WEEK]?.is_required ??
      getShouldShowQuestion(requirements, QuestionKey.WORKING_HOURS_PER_WEEK),
  },
  [QuestionKey.LANGUAGES]: {
    ...requirements[QuestionKey.LANGUAGES],
    is_required:
      requirements[QuestionKey.LANGUAGES]?.is_required ?? getShouldShowQuestion(requirements, QuestionKey.LANGUAGES),
  },
});

export const useQuestionnaire = ({ jobId }: UseQuestionnaireParams) => {
  const [questionnaireState, setQuestionnaireState] = useState<CandidateRequirementsDTO | null>(null);

  const { data: config, isLoading } = useQuestionnaireConfigurationQuery(jobId ?? 0);

  useEffect(() => {
    if (config?.questionnaire_requirements) {
      const requirements = questionnaireRequirementsToCandidateRequirements(config.questionnaire_requirements);
      setQuestionnaireState(buildQuestionnaireState(requirements));
    }
  }, [config?.questionnaire_requirements]);

  const updateQuestionnaireRequirements = useCallback(
    <K extends QuestionKey>(questionKey: K, jobRequirements: CandidateRequirementsDTO[K]['job_requirements']) => {
      setQuestionnaireState((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          [questionKey]: {
            ...prev[questionKey],
            job_requirements: jobRequirements,
          },
        };
      });
    },
    [],
  );

  const updateQuestionnaireFieldUnit = useCallback(
    <K extends QuestionKey>(questionKey: K, unit: CandidateRequirementsDTO[K]['unit']) => {
      setQuestionnaireState((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          [questionKey]: {
            ...prev[questionKey],
            unit: unit,
          },
        };
      });
    },
    [],
  );

  const updateQuestionnaireRequired = useCallback(<K extends QuestionKey>(questionKey: K, isRequired: boolean) => {
    setQuestionnaireState((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        [questionKey]: {
          ...prev[questionKey],
          is_required: isRequired,
        },
      };
    });
  }, []);

  const shouldShowQuestion = useCallback(
    (questionKey: QuestionKey) => {
      return getShouldShowQuestion(questionnaireState ?? undefined, questionKey);
    },
    [questionnaireState],
  );

  return {
    questions: questionnaireState,
    questionnaireState,
    updateQuestionnaireRequirements,
    updateQuestionnaireRequired,
    updateQuestionnaireFieldUnit,
    isLoading,
    shouldShowQuestion,
    pendingReview: config?.pending_review === true,
  };
};

export type QuestionnaireRequirement = CandidateRequirementsDTO[QuestionKey];

const getShouldShowQuestion = (requirements: CandidateRequirementsDTO | undefined, questionKey: QuestionKey) => {
  if (!requirements) return false;
  const shouldShow = requirements[questionKey].job_requirements;

  if (!shouldShow || (Array.isArray(shouldShow) && shouldShow.length === 0)) return false;

  return true;
};
